"use client";
import Link from "next/link";
import { useEffect, useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import useCountDown from "@core/helpers/client/hooks/useCountDown";
import SwiperComponent from "@/components/ui/swiper-component";
import ProductCard from "@/components/ProductCard";

export default function AmazingOffers({ fields, collection_data, limit = 10 }) {
    // کارت‌ها همراه ویجت از کوئری صفحه (getPrefixUrlDetails) می‌آیند؛ fetch کلاینتی نداریم
    const products = collection_data?.products || [];
    const extra_data = collection_data?.collection?.extra_fields || null;

    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        setHydrated(true);
    }, []);

    const expireTimestamp = useMemo(() => {
        if (!extra_data?.expireAt) return null;
        return parseInt(extra_data.expireAt);
    }, [extra_data]);

    const timer = useMemo(() => {
        if (!expireTimestamp) return 0;
        return Math.max(0, expireTimestamp - Date.now());
    }, [expireTimestamp]);

    const { counter_down, convert_countdown_time } = useCountDown({
        number: timer ? Math.floor(timer / 1000) : 0,
        countdown_className: "amazing-offers-countdown flex items-center justify-center gap-1 lg:gap-2 dir-ltr",
    });

    return (
        <>
            <div className="block lg:hidden">
                <div className="bg-linear-225 from-jmt-black to-jmt-neutral-900 py-4 w-full">

                    {/* Header */}
                    <div className="flex items-center justify-between px-4 md:px-10 pt-1 pb-5">
                        <div className="flex items-center text-jmt-white gap-2">
                            <div className="flex items-center gap-1">
                                <div
                                    className="text-editor-svg"
                                    dangerouslySetInnerHTML={{ __html: fields?.icon }}
                                />

                                <div
                                    className="text-editor-title"
                                    dangerouslySetInnerHTML={{ __html: fields?.title }}
                                />
                            </div>

                            {hydrated &&
                                fields?.navbar &&
                                (expireTimestamp && timer > 0 ? (
                                    convert_countdown_time(counter_down, {
                                        formats: ["hours", "minutes", "seconds"],
                                        convertDaysToHours: true,
                                    })
                                ) : expireTimestamp ? (
                                    <span className="flex items-center justify-center text-jmt-white text-sm font-bold">
                                        به اتمام رسید!
                                    </span>
                                ) : null)}
                        </div>

                        {fields?.["has-link"] && (
                            <Link
                                href={fields?.link?.url || "#"}
                                className="flex items-center text-jmt-white text-xs lg:text-sm font-bold gap-1 whitespace-nowrap"
                            >
                                همه
                                <ChevronLeft size={18} />
                            </Link>
                        )}
                    </div>

                    <SwiperComponent
                        slides={products.length > 0 ? products?.slice(0, limit)?.map((product, index) => (
                            <div
                                key={`product--key${product.id}`}
                                className="h-full bg-white border border-jmt-neutral-200 rounded-md overflow-hidden"
                            >
                                <ProductCard product={product} index={index} />
                            </div>
                        )) : []}
                        breakpoints={{
                            380: { slidesPerView: 2.1, spaceBetween: 8 },
                            500: { slidesPerView: 2.8, spaceBetween: 8 },
                            640: { slidesPerView: 3, spaceBetween: 8 },
                            767: { slidesPerView: 3.5, spaceBetween: 12 },
                            920: { slidesPerView: 4, spaceBetween: 12 },
                            1024: { slidesPerView: 4.5, spaceBetween: 16 },
                            1280: { slidesPerView: 5.5, spaceBetween: 16 },
                        }}
                        slidesPerView={1.5}
                        spaceBetween={8}
                        navigation={false}
                        swiper_className="px-4! md:px-10!"
                        loop={false}
                        pagination={false}
                        grabCursor={true}
                    />
                </div>
            </div>

            <div className="hidden lg:block">
                <div className="container">
                    <div className="bg-linear-225 from-jmt-black to-jmt-neutral-900 rounded-md px-4 py-6 w-full">

                        {/* Header Section */}
                        <div className="flex items-center justify-between mb-6 px-2">
                            <div className="flex flex-col sm:flex-row sm:items-center text-jmt-white space-x-6">
                                <div className="flex items-center gap-1">
                                    <div className="text-editor-svg" dangerouslySetInnerHTML={{
                                        __html: fields?.icon
                                    }} />

                                    <div className="text-editor-title" dangerouslySetInnerHTML={{
                                        __html: fields?.title
                                    }} />
                                </div>

                                {fields?.navbar && (expireTimestamp && timer > 0 ? (
                                    convert_countdown_time(counter_down, {
                                        formats: ["hours", "minutes", "seconds"],
                                        convertDaysToHours: true,
                                    })
                                ) : expireTimestamp ? (
                                    <span className="text-jmt-white text-base font-bold">
                                        به اتمام رسید!
                                    </span>
                                ) : null)}
                            </div>

                            {fields?.["has-link"] ?
                                <Link href={fields?.link?.url || "#"} className="flex items-center gap-1 text-jmt-white text-sm font-bold opacity-90 hover:opacity-100 transition-opacity whitespace-nowrap">
                                    {fields?.["text-link"]}
                                    <ChevronLeft size={20} />
                                </Link>
                                : ""}
                        </div>

                        <SwiperComponent
                            slides={products.length > 0 ? products.slice(0, limit).map((product, index) => (
                                <div
                                    key={`product--key${product.id}`}
                                    className="h-full bg-white border border-jmt-neutral-200 rounded-md overflow-hidden"
                                >
                                    <ProductCard product={product} index={index} />
                                </div>
                            )) : []}
                            breakpoints={{
                                380: { slidesPerView: 2.1, spaceBetween: 8 },
                                500: { slidesPerView: 2.8, spaceBetween: 8 },
                                640: { slidesPerView: 3, spaceBetween: 8 },
                                767: { slidesPerView: 3.5, spaceBetween: 12 },
                                920: { slidesPerView: 4, spaceBetween: 12 },
                                1024: { slidesPerView: 4.5, spaceBetween: 16 },
                                1280: { slidesPerView: 5.5, spaceBetween: 16 },
                            }}
                            slidesPerView={1.5}
                            spaceBetween={8}
                            navigation={true}
                            navPosition="sides"
                            loop={false}
                            pagination={false}
                            grabCursor={true}
                        />
                    </div>
                </div>

            </div>
        </>
    );

}



